/**
 * Simple storage for environment settings.
 *
 * File: \src\EnvironmentHandler.ts
 * Created Date: Saturday, May 9th 2020, 12:56:22
 * Author: Sascha Herrmann
 * -----
 * Last Modified: November 9th 2024, 14:32:40
 * Modified By: Herrmann Sascha (LDC)
 * -----
 * Copyright (c) 2017-2020 Liebherr-Hausgeräte GmbH
 */

import { CubeLogger } from '@/Logger';

/**
 * Environment name
 *
 * @enum {number}
 */
enum EnvName {
  LOCALHOST = 'localhost',
  LOCALFORWARD = 'localforward',
  DEVELOPMENT = 'development',
  STABLE = 'stable',
  PRODUCTION = 'production'
}

type ENV = {
  KEY: string;
  VALUE: string;
};

export default class EnvironmentHandler {
  // The REST URL for localforward mode.
  private static readonly LOCALFORWARD_REST_URL = 'http://localhost:8081/ui';

  private static readonly ENVS: ENV[] = [
    // Liebherr
    {
      KEY: '63382d99-3566-4db9-ac3c-fd9c7f654586',
      VALUE:
        'ODcxOGY2ZTItNWZjMS00MjZmLTlmNjAtODU4OGY5MmRkMGM4QEBiP0Q5U2x5VUQhakNKTXJuU2FKY005W25RVGs2Yk4helkpWHBTbH5sd0hMTTU='
    },
    {
      KEY: 'cf920055-9c04-47e7-af13-ddafd76dddea',
      VALUE:
        'YjZiMDczMWUtMDQ0Ni00MTY0LThhOGEtY2VlMjY3NjQ5NDQyQEBiXTVBQ0VGSHctMHdiUjkxNlJ4a3JycllOTVIkajBZY0ZTOVFkekFTSHFiXTg='
    },
    {
      KEY: '6023fdb3-4000-47df-97ce-eeb1a10c7be6',
      VALUE:
        'ZTZkNTk4YzAtZGY0ZS00YzY1LWJmYzUtM2E0MDY4MjMxY2FmQEA/TTRiQWNtZ01STjRZNjZ5OGhjQltXQHBETX44OWZMYnNYR2pDQTJ4UFooWjc='
    },
    {
      KEY: '349f4464-c7e3-43d6-a86d-d2b4de673890',
      VALUE:
        'MzQ2YTEwNmMtZjcxMy00YzEyLThiNGEtZWQwOWU0M2NiYmQ1QEBVWUxQUjl6Y3JnZGpmUjRrKnNhdE1XTkBLUzJZKUdkbnd+cHlZWlF5c0p+bnU='
    }
  ];

  /**
   * This thing can only be initialized once.
   *
   * @private
   * @type {boolean}
   * @memberof EnvironmentHandler
   */
  private static initialized: boolean = false;

  /**
   * The name of the environment.
   *
   * @private
   * @static
   * @type {string}
   * @memberof EnvironmentHandler
   */
  private static _NAME: string;
  public static get NAME(): string {
    return EnvironmentHandler._NAME;
  }

  /**
   * The base URI for the environment.
   *
   * @private
   * @static
   * @type {string}
   * @memberof EnvironmentHandler
   */
  private static _BASE_URI: string;
  public static get BASE_URI(): string {
    return EnvironmentHandler._BASE_URI;
  }

  /**
   * The URL for the backend calls.
   *
   * @private
   * @static
   * @type {string}
   * @memberof EnvironmentHandler
   */
  private static _REST_URL: string;
  public static get REST_URL(): string {
    return EnvironmentHandler._REST_URL;
  }

  /**
   * The LIS login server.
   *
   * @private
   * @static
   * @type {string}
   * @memberof EnvironmentHandler
   */
  private static _AUTHORITY: string;
  public static get AUTHORITY(): string {
    return EnvironmentHandler._AUTHORITY;
  }

  /**
   * Carries the ID of the client.
   *
   * @private
   * @static
   * @type {string}
   * @memberof EnvironmentHandler
   */
  private static _CLIENT_ID: string;
  public static get CLIENT_ID(): string {
    return EnvironmentHandler._CLIENT_ID;
  }

  /**
   * Carries the secret of the client.
   *
   * @private
   * @static
   * @type {string}
   * @memberof EnvironmentHandler
   */
  private static _CLIENT_SECRET: string;
  public static get CLIENT_SECRET(): string {
    return EnvironmentHandler._CLIENT_SECRET;
  }

  /**
   * The URL that the LIS redirects to after a successful login.
   *
   * @private
   * @static
   * @type {string}
   * @memberof EnvironmentHandler
   */
  private static _REDIRECT_URI: string;
  public static get REDIRECT_URI(): string {
    return EnvironmentHandler._REDIRECT_URI;
  }

  /**
   * OAuth stuff.
   *
   * @private
   * @static
   * @type {string}
   * @memberof EnvironmentHandler
   */
  private static _RESPONSE_TYPE: string;
  public static get RESPONSE_TYPE(): string {
    return EnvironmentHandler._RESPONSE_TYPE;
  }

  /**
   * The scope controls for the client available features.
   *
   * @private
   * @static
   * @type {string}
   * @memberof EnvironmentHandler
   */
  private static _SCOPE: string;
  public static get SCOPE(): string {
    return EnvironmentHandler._SCOPE;
  }

  /**
   * The silent redirect URI used during login (time) extensions.
   *
   * @private
   * @static
   * @type {string}
   * @memberof EnvironmentHandler
   */
  private static _SILENT_REDIRECT_URI: string;
  public static get SILENT_REDIRECT_URI(): string {
    return EnvironmentHandler._SILENT_REDIRECT_URI;
  }

  /**
   * Inits the environment.
   *
   * @static
   * @param {*} json The json to initialize with.
   *
   * @memberof EnvironmentHandler
   */
  public static init(json: any) {
    const logger = CubeLogger.$log.prefix('Environment::init');
    logger.debug('Initializing environment');

    if (EnvironmentHandler.initialized) {
      logger.debug('Already initialized');

      return;
    }

    EnvironmentHandler._NAME = this.getEnvironmentName(json.NAME, import.meta.env.VITE_ENVIRONMENT);

    if (!EnvironmentHandler._NAME) {
      logger.error('Wrong environment');

      return;
    }

    this.initOidc(json);

    EnvironmentHandler._BASE_URI = json.BASE_URI;
    EnvironmentHandler._REST_URL = json.REST_URL;

    // There is no separate env file for the localforward mode, which is like localhost but just uses the
    // local server running in IntelliJ as backend:
    if (EnvironmentHandler._NAME === EnvName.LOCALFORWARD) {
      logger.debug('Switching to localforward mode');

      EnvironmentHandler._REST_URL = this.LOCALFORWARD_REST_URL;
    }

    EnvironmentHandler.initialized = true;
  }

  /**
   * Inits the OIDC settings.
   *
   * @static
   * @param {*} json The json to initialize with.
   *
   * @memberof EnvironmentHandler
   */
  public static initOidc(json: any) {
    // KEY signals the enviorment
    const KEY = json.KEY;

    const value = this.ENVS.find(env => env.KEY === KEY);
    const decoded: string = atob(value.VALUE);
    const decodedParts: string[] = decoded.split('@@');

    EnvironmentHandler._AUTHORITY = json.AUTHORITY;
    EnvironmentHandler._CLIENT_ID = decodedParts[0];
    EnvironmentHandler._CLIENT_SECRET = decodedParts[1];
    EnvironmentHandler._REDIRECT_URI = json.REDIRECT_URI;
    EnvironmentHandler._RESPONSE_TYPE = json.RESPONSE_TYPE;
    EnvironmentHandler._SCOPE = json.SCOPE;
    EnvironmentHandler._SILENT_REDIRECT_URI = json.SILENT_REDIRECT_URI;
  }

  /**
   * Checks whether the environment config matches the local environment name.
   *
   * @private
   * @static
   * @param {string} environmentName The name of the environment read from the environment JSON.
   * @param {string} vueAppEnvironmentName The name of the environment coming from the VITE setting.
   * @returns {string} The checked environment name.
   *
   * @memberof EnvironmentHandler
   */
  private static getEnvironmentName(environmentName: string, vueAppEnvironmentName: string): string {
    if (environmentName === EnvName.LOCALHOST && vueAppEnvironmentName === EnvName.LOCALFORWARD) {
      return EnvName.LOCALFORWARD;
    }

    // release, might come from stable and production
    if (
      (vueAppEnvironmentName === 'release' &&
        (environmentName === EnvName.STABLE || environmentName === EnvName.PRODUCTION)) ||
      vueAppEnvironmentName === environmentName // INT and DEV
    ) {
      return environmentName;
    }

    return undefined;
  }

  /**
   * Checks whether we're running in local environment (localhost or localforward).
   *
   * @static
   * @return {*}  {boolean} true if the environment is local.
   *
   * @memberof EnvironmentHandler
   */
  public static isLocalEnvironment(): boolean {
    return EnvironmentHandler.NAME === EnvName.LOCALFORWARD || EnvironmentHandler.NAME === EnvName.LOCALHOST;
  }

  /**
   * Checks whether we are running in development mode (either local or DEV).
   *
   * @static
   * @return {*}  {boolean} true if environment is local or DEV.
   * @memberof EnvironmentHandler
   */
  public static isDevelopmentEnvironment(): boolean {
    return EnvironmentHandler.NAME === EnvName.DEVELOPMENT || EnvironmentHandler.isLocalEnvironment();
  }

  /**
   * Checks whether we are running in Production or Stable environment
   *
   * @static
   * @return {*}  {boolean} true if environment is Production
   * @memberof EnvironmentHandler
   */
  public static isStableOrProdEnvironment(): boolean {
    return EnvironmentHandler.NAME === EnvName.STABLE || EnvironmentHandler.NAME === EnvName.PRODUCTION;
  }

  /**
   * Checks whether we are running in Stable environment
   *
   * @static
   * @return {*}  {boolean} true if environment is stable
   * @memberof EnvironmentHandler
   */
  public static isStableEnvironment(): boolean {
    return EnvironmentHandler.NAME === EnvName.STABLE;
  }

  /**
   * Checks whether we are running in Production environment
   *
   * @static
   * @return {*}  {boolean}
   * @memberof EnvironmentHandler
   */
  public static isProdEnvironment(): boolean {
    return EnvironmentHandler.NAME === EnvName.PRODUCTION;
  }
}
